import * as React from 'react';
import { StylesConfig } from 'react-select';
import AsyncSelect from 'react-select/async';

import { SchoolOption, SchoolNameAutocompleteProps } from './types';

/**
 * 学校名予測入力コンポーネント
 * react-select の AsyncSelect を使用して、APIから学校情報を非同期検索
 */
const SchoolNameAutocomplete: React.FC<SchoolNameAutocompleteProps> = ({
  schoolType,
  value,
  onChange,
  isDisabled = false,
  hasError = false
}) => {
  /**
   * ProfileのschoolTypeをSchoolMasterのschoolTypeに変換
   * Profile enum: university(1), junior_college(2), agricultural_college(3)
   * SchoolMaster enum: university(0), junior_college(1), agricultural_college(3)
   */
  const convertSchoolType = (profileSchoolType: string): string => {
    const mapping: { [key: string]: string } = {
      '1': '0', // university: Profile(1) → SchoolMaster(0)
      '2': '1', // junior_college: Profile(2) → SchoolMaster(1)
      '3': '3' // agricultural_college: Profile(3) → SchoolMaster(3)
    };
    return mapping[profileSchoolType] || profileSchoolType;
  };

  /**
   * 学校名を検索する関数
   * @param inputValue - ユーザーが入力した文字列
   * @returns Promise<SchoolOption[]> - 検索結果の配列
   */
  const loadOptions = async (inputValue: string): Promise<SchoolOption[]> => {
    // 2文字未満の場合は検索しない
    if (!inputValue || inputValue.length < 2) {
      return [];
    }

    try {
      // ProfileのschoolTypeをSchoolMasterのschoolTypeに変換
      const schoolMasterType = convertSchoolType(schoolType);

      // APIエンドポイントにリクエスト
      const response = await fetch(
        `/api/school_masters/search?query=${encodeURIComponent(inputValue)}&school_type=${schoolMasterType}`
      );

      if (!response.ok) {
        console.error('School search API error:', response.statusText);
        return [];
      }

      const data: SchoolOption[] = await response.json();
      return data;
    } catch (error) {
      console.error('School search error:', error);
      return [];
    }
  };

  /**
   * カスタムスタイル
   */
  const customStyles: StylesConfig<SchoolOption> = {
    control: (base, state) => ({
      ...base,
      borderColor: hasError ? '#e74c3c' : state.isFocused ? '#4CAF50' : '#ddd',
      boxShadow: hasError ? '0 0 0 1px #e74c3c' : state.isFocused ? '0 0 0 1px #4CAF50' : 'none',
      '&:hover': {
        borderColor: hasError ? '#e74c3c' : '#4CAF50'
      },
      minHeight: '45px',
      fontSize: '16px'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#999'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#f0f8f0' : 'white',
      color: '#333',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#e0f0e0'
      }
    }),
    menu: (base) => ({
      ...base,
      zIndex: 100
    })
  };

  return (
    <AsyncSelect<SchoolOption>
      cacheOptions
      defaultOptions={false}
      loadOptions={loadOptions}
      onChange={onChange}
      value={value}
      isDisabled={isDisabled}
      placeholder="例) 〇〇大学"
      noOptionsMessage={({ inputValue }) => {
        if (!inputValue || inputValue.length < 2) {
          return '2文字以上入力してください';
        }
        return '該当する学校が見つかりませんでした';
      }}
      loadingMessage={() => '検索中...'}
      styles={customStyles}
      isClearable
    />
  );
};

export default SchoolNameAutocomplete;
